import GeneralService from "./GeneralService";
import { ApiKeyOrGetter } from "./GeneralService";
import { Component } from "react";

export interface WorkflowRun {
  id: number;
  date_created?: string;
  date_updated?: string;
  workflow_id: number;
  company_id: number;
  application_id?: number;
  session_id: string;
  status: string;
  input?: Record<string, unknown>;
  output?: Record<string, unknown>;
  error_message?: string;
  profile?: Record<string, unknown>;
  meta?: Record<string, unknown>;
  workflow_name?: string;
}

export interface WorkflowSchemaField {
  type: string;
  description?: string;
  required?: boolean;
}

export interface Workflow {
  id: number;
  date_created?: string;
  date_updated?: string;
  workflow_name: string;
  workflow_ref_name: string;
  application_id?: number;
  description?: string;
  timeout_seconds?: number;
  status?: string;
  meta?: {
    input_schema?: Record<string, WorkflowSchemaField>;
    output_schema?: Record<string, WorkflowSchemaField>;
    [key: string]: unknown;
  };
}

export interface WorkflowTestSetItem {
  id: number;
  date_created?: string;
  test_set_id: number;
  company_id: number;
  workflow_run_id: number;
  meta?: Record<string, unknown>;
  workflow_run?: WorkflowRun;
}

export interface WorkflowTestSet {
  id: number;
  date_created?: string;
  date_updated?: string;
  company_id: number;
  application_id: number;
  test_set_name: string;
  test_set_description?: string;
  meta?: Record<string, unknown>;
  item_count: number;
  application_name?: string;
  items: WorkflowTestSetItem[];
}

export interface WorkflowTestRun {
  id: number;
  date_created?: string;
  date_completed?: string;
  test_set_id: number;
  workflow_id: number;
  company_id: number;
  description?: string;
  status: string;
  config?: Record<string, unknown>;
  meta?: Record<string, unknown>;
  workflow_name?: string;
  item_count: number;
  completed_count: number;
  passed_count: number;
  failed_count: number;
  error_count: number;
  duration_ms?: { avg?: number; p50?: number; p90?: number; p95?: number };
  avg_field_match_rate?: number;
}

export interface DiffField {
  path: string;
  status: "match" | "mismatch" | "missing" | "extra";
  expected: unknown;
  actual: unknown;
}

export interface DiffResult {
  fields: DiffField[];
  matched_count: number;
  total_count: number;
  match_rate: number;
}

export interface WorkflowTestRunItem {
  id: number;
  date_created?: string;
  date_executed?: string;
  test_run_id: number;
  test_set_item_id: number;
  company_id: number;
  status: string;
  expected_output?: Record<string, unknown>;
  actual_output?: Record<string, unknown>;
  matched?: boolean;
  error_message?: string;
  duration_ms?: number;
  diff_result?: DiffResult;
  input?: Record<string, unknown>;
}

export class WorkflowService extends Component {
  public static getWorkflows(
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      "/workflow/",
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getWorkflow(
    workflowId: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/workflow/${workflowId}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static createWorkflow(
    data: {
      workflow_name: string;
      workflow_ref_name: string;
      script: string;
      description?: string;
      timeout_seconds?: number;
      application_id?: number;
      input_schema?: string;
      output_schema?: string;
      transform_session?: string;
      transform_enabled?: boolean;
    },
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      "/workflow/",
      JSON.stringify(data),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getWorkflowScript(
    workflowId: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/workflow/${workflowId}/script`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static updateWorkflow(
    workflowId: number,
    data: {
      workflow_name?: string;
      script?: string;
      description?: string;
      timeout_seconds?: number;
      application_id?: number;
      input_schema?: string;
      output_schema?: string;
      transform_session?: string;
      transform_enabled?: boolean;
    },
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.put(
      `/workflow/${workflowId}`,
      JSON.stringify(data),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static deleteWorkflow(
    workflowId: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.delete(
      `/workflow/${workflowId}`,
      JSON.stringify({}),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static validateWorkflowScript(
    code: string,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      "/workflow/validate",
      JSON.stringify({ code }),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Run workflow via main API POST /workflow/run-completion, which executes
   * server-side `MaitaiAsync.workflow.completions.create` against the workflow service.
   */
  public static runWorkflowCompletion(
    body: Record<string, unknown>,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter,
    includeProfile?: boolean
  ) {
    const postOptions: any = structuredClone(GeneralService.options);
    postOptions.method = "POST";
    postOptions.body = JSON.stringify(body);
    if (includeProfile) {
      postOptions.headers["X-Profile"] = "true";
    }
    return GeneralService.fetchHandler(
      "/workflow/run-completion",
      postOptions,
      loadingHandler ?? (() => {}),
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Same as {@link runWorkflowCompletion} but returns a Promise (plain fetch).
   * Prefer this from UI code that must not lose results when the Observable is torn down
   * (e.g. React Strict Mode or tab conditional mount).
   */
  public static async runWorkflowCompletionAsync(
    body: Record<string, unknown>,
    apiKey: ApiKeyOrGetter,
    includeProfile?: boolean,
    signal?: AbortSignal
  ): Promise<unknown> {
    const postOptions: Record<string, unknown> = structuredClone(GeneralService.options);
    postOptions.method = "POST";
    postOptions.body = JSON.stringify(body);
    if (signal !== undefined) {
      postOptions.signal = signal;
    }
    const headers = postOptions.headers as Record<string, string>;
    if (includeProfile) {
      headers["X-Profile"] = "true";
    }

    let tokenString: string;
    if (typeof apiKey === "string") {
      tokenString = apiKey;
    } else if (typeof apiKey === "object" && apiKey !== null) {
      tokenString = await apiKey.getToken();
    } else {
      throw new Error("Invalid API key type");
    }

    if (typeof apiKey === "object") {
      headers["Authorization"] = `Bearer ${tokenString}`;
      delete headers["x-api-key"];
    } else {
      headers["x-api-key"] = tokenString;
      delete headers["Authorization"];
    }

    const fullPath = `${GeneralService.PROD_ENDPOINT}/workflow/run-completion`;
    const response = await fetch(fullPath, postOptions as RequestInit);
    const data = await GeneralService.handleResponse(response, JSON);
    if (!response.ok) {
      throw data;
    }
    return data;
  }

  public static getDefaultWorkflowScript(
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      "/workflow/default-script",
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getWorkflowVersions(
    workflowId: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/workflow/${workflowId}/versions`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static deployWorkflowVersion(
    workflowId: number,
    versionId: string,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      `/workflow/${workflowId}/versions/${encodeURIComponent(versionId)}/deploy`,
      JSON.stringify({}),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static createWorkflowTestSet(
    applicationId: number,
    testSetName: string,
    workflowRunIds: number[] | null,
    sessionIds: string[] | null,
    workflowId: number | null,
    testSetDescription: string | undefined,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    const body: Record<string, unknown> = {
      application_id: applicationId,
      test_set_name: testSetName,
    };
    if (workflowId) {
      body.workflow_id = workflowId;
    }
    if (testSetDescription) {
      body.test_set_description = testSetDescription;
    }
    if (workflowRunIds && workflowRunIds.length > 0) {
      body.workflow_run_ids = workflowRunIds;
    }
    if (sessionIds && sessionIds.length > 0) {
      body.session_ids = sessionIds;
    }
    return GeneralService.post(
      "/workflow/test-sets",
      JSON.stringify(body),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getWorkflowRunsByWorkflow(
    workflowId: number,
    offset: number,
    limit: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/workflow/runs?workflow_id=${workflowId}&offset=${offset}&limit=${limit}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getWorkflowRuns(
    applicationId: number,
    offset: number,
    limit: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter,
    workflowIds?: number[]
  ) {
    let url = `/workflow/runs?application_id=${applicationId}&offset=${offset}&limit=${limit}`;
    if (workflowIds && workflowIds.length > 0) {
      url += `&workflow_ids=${workflowIds.join(",")}`;
    }
    return GeneralService.get(
      url,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getWorkflowRun(
    sessionId: string,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    const encodedSessionId = encodeURIComponent(sessionId);
    return GeneralService.get(
      `/workflow/runs/${encodedSessionId}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static checkWorkflowTransforms(
    workflowId: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/workflow/${workflowId}/has-transforms`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static transformPreview(
    workflowId: number,
    sessionId: string,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      `/workflow/${workflowId}/transform-preview`,
      JSON.stringify({ session_id: sessionId }),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getWorkflowTestSet(
    testSetId: number,
    limit: number,
    offset: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/workflow/test-sets/${testSetId}?limit=${limit}&offset=${offset}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static updateWorkflowTestSet(
    testSetId: number,
    name: string,
    description: string | undefined,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    const body: Record<string, unknown> = { test_set_name: name };
    if (description !== undefined) {
      body.test_set_description = description;
    }
    return GeneralService.put(
      `/workflow/test-sets/${testSetId}`,
      JSON.stringify(body),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static addWorkflowTestSetItems(
    testSetId: number,
    workflowRunIds: number[] | null,
    sessionIds: string[] | null,
    workflowId: number | null,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    const body: Record<string, unknown> = {};
    if (workflowRunIds && workflowRunIds.length > 0) {
      body.workflow_run_ids = workflowRunIds;
    }
    if (sessionIds && sessionIds.length > 0) {
      body.session_ids = sessionIds;
    }
    if (workflowId) {
      body.workflow_id = workflowId;
    }
    return GeneralService.post(
      `/workflow/test-sets/${testSetId}/items`,
      JSON.stringify(body),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getImportedSessionIds(
    testSetId: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/workflow/test-sets/${testSetId}/imported-session-ids`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static deleteWorkflowTestSetItem(
    testSetId: number,
    itemId: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.delete(
      `/workflow/test-sets/${testSetId}/items/${itemId}`,
      JSON.stringify({}),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static createWorkflowTestRun(
    testSetId: number,
    workflowId: number | undefined,
    description: string | undefined,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    const body: Record<string, unknown> = { test_set_id: testSetId };
    if (workflowId) body.workflow_id = workflowId;
    if (description) body.description = description;
    return GeneralService.post(
      "/workflow/test-runs",
      JSON.stringify(body),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getWorkflowTestRuns(
    testSetId: number,
    offset: number,
    limit: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/workflow/test-runs/by-test-set/${testSetId}?offset=${offset}&limit=${limit}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getWorkflowTestRun(
    testRunId: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/workflow/test-runs/${testRunId}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getWorkflowTestRunsComparison(
    testRunIds: number[],
    offset: number,
    limit: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      "/workflow/test-runs/compare",
      JSON.stringify({ test_run_ids: testRunIds, offset, limit }),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getWorkflowTestRunProgress(
    testRunId: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/workflow/test-runs/${testRunId}/progress`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getWorkflowTestRunItems(
    testRunId: number,
    offset: number,
    limit: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter,
    matchedFilter?: boolean | null
  ) {
    let url = `/workflow/test-runs/${testRunId}/items?offset=${offset}&limit=${limit}`;
    if (matchedFilter === true) url += "&matched=true";
    else if (matchedFilter === false) url += "&matched=false";
    return GeneralService.get(
      url,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static deleteWorkflowTestRun(
    testRunId: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.delete(
      `/workflow/test-runs/${testRunId}`,
      JSON.stringify({}),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static deleteWorkflowTestSet(
    testSetId: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.delete(
      `/workflow/test-sets/${testSetId}`,
      JSON.stringify({}),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }
}
